import { Datasource } from './Datasource';

export type Dataset = {
  '@id': string;
  id: DatasetId;
  name: string;
  sources: Datasource[];
};

export enum DatasetId {
  MODIS = 'MODIS',
  DEM = 'DEM',
  L8L1C = 'L8L1C',
  S2L1C = 'S2L1C',
  S1GRD = 'S1GRD',
  S3OLCI = 'S3OLCI',
  S3SLSTR = 'S3SLSTR',
  S5PL2 = 'S5PL2',
  S2L2A = 'S2L2A',
  BYOC = 'CUSTOM',
}

export const supportsBackCoeff = (datasetId: DatasetId) => {
  return datasetId === DatasetId.S1GRD;
};

export const supportsAtmosphericCorrection = (datasetId: DatasetId) => {
  return datasetId === DatasetId.S2L1C;
};

export const supportsTemporal = (datasetId: DatasetId) => {
  return datasetId !== DatasetId.DEM;
};

export const supportsMosaicOrder = (datasetId: DatasetId) => {
  return datasetId !== DatasetId.DEM;
};

export const supportsTimeRange = (datasetId: DatasetId) => {
  return datasetId !== DatasetId.DEM;
};

export const supportsCloudCoverage = (datasetId: DatasetId) => {
  return (
    datasetId === DatasetId.S2L1C ||
    datasetId === DatasetId.S2L2A ||
    datasetId === DatasetId.L8L1C ||
    datasetId === DatasetId.S3SLSTR
  );
};

export const supportsPreviewMode = (datasetId: DatasetId) => {
  return datasetId === DatasetId.S2L1C || datasetId === DatasetId.S2L2A;
};

export const supportsEGM = (datasetId: DatasetId) => {
  return datasetId === DatasetId.DEM;
};

export const supportsClampNegative = (datasetId: DatasetId) => {
  return datasetId === DatasetId.DEM;
};

export enum Polarization {
  DV = 'DV',
  SH = 'SH',
  SV = 'SV',
  DH = 'DH',
}

export enum DemInstance {
  MAPZEN = 'MAPZEN',
  COPERNICUS = 'COPERNICUS',
  COPERNICUS_30 = 'COPERNICUS_30',
  COPERNICUS_90 = 'COPERNICUS_90',
}

export enum OrbitDirection {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
  BOTH = 'BOTH',
}

export enum AcquisitionMode {
  IW = 'IW',
  EW = 'EW',
}

export enum Resolution {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
}

export enum S3SlstrView {
  NADIR = 'NADIR',
  OBLIQUE = 'OBLIQUE',
}

export const S1_RESOLUTION_OPTIONS = [
  { value: Resolution.HIGH, text: 'High', key: Resolution.HIGH },
  { value: Resolution.MEDIUM, text: 'Medium', key: Resolution.MEDIUM },
];

export const S1_ACQUISITION_MODE_OPTIONS = [
  { value: AcquisitionMode.IW, text: AcquisitionMode.IW, key: AcquisitionMode.IW },
  { value: AcquisitionMode.EW, text: AcquisitionMode.EW, key: AcquisitionMode.EW },
];

export const ORBIT_DIRECTION_OPTIONS = [
  { value: OrbitDirection.BOTH, text: 'Both', key: OrbitDirection.BOTH },
  { value: OrbitDirection.ASCENDING, text: 'Ascending', key: OrbitDirection.ASCENDING },
  { value: OrbitDirection.DESCENDING, text: 'Descending', key: OrbitDirection.DESCENDING },
];

export const S1_POLARIZATION_OPTIONS = [
  { value: Polarization.DV, text: 'VV + VH', key: Polarization.DV },
  { value: Polarization.SH, text: 'HH', key: Polarization.SH },
  { value: Polarization.SV, text: 'VV', key: Polarization.SV },
  { value: Polarization.DH, text: 'HH + HV', key: Polarization.DH },
];

export const DEM_INSTANCE_OPTIONS = [
  { value: DemInstance.MAPZEN, text: 'Mapzen DEM', key: 'mapzen' },
  { value: DemInstance.COPERNICUS_30, text: 'Copernicus DEM 30/90m', key: 'copernicus_30' },
  { value: DemInstance.COPERNICUS_90, text: 'Copernicus DEM 90m', key: 'copernicus_90' },
];

export const ORTHORECTIFY_OPTIONS = [
  { value: false, text: 'Disabled', key: 'disabled' },
  { value: DemInstance.MAPZEN, text: 'Yes - using Mapzen DEM', key: 'mapzen' },
  { value: DemInstance.COPERNICUS, text: 'Yes - using Copernicus 10/30m DEM', key: 'copernicus' },
  {
    value: DemInstance.COPERNICUS_30,
    text: 'Yes - using Copernicus 30m DEM',
    key: 'copernicus_30',
  },
  {
    value: DemInstance.COPERNICUS_90,
    text: 'Yes - using Copernicus 90m DEM',
    key: 'copernicus_90',
  },
];

export const BACK_COEFF_OPTIONS = [
  { value: 'BETA0', text: 'beta0', key: 'beta0' },
  { value: 'SIGMA0_ELLIPSOID', text: 'sigma0', key: 'sigma0' },
  { value: 'GAMMA0_ELLIPSOID', text: 'gamma0', key: 'gamma0' },
  { value: 'GAMMA0_TERRAIN', text: 'gamma0 (radiometric terrain corrected)', key: 'gamma0_rtc' },
];

export const S3SLSTR_VIEW_OPTIONS = [
  { value: S3SlstrView.NADIR, text: 'Nadir', key: S3SlstrView.NADIR },
  { value: S3SlstrView.OBLIQUE, text: 'Oblique', key: S3SlstrView.OBLIQUE },
];

export const BYOC_SUBTYPE_OPTIONS = [
  { value: 'BATCH', text: 'Batch', key: 'BATCH' },
  { value: 'BYOC', text: 'BYOC', key: 'BYOC' },
];
